"use client";

/**
 * 🔴 2026-10-22（用户：「这个声音开关是全局生效的，状态也需要落库」）
 *
 * 视频声音开关的**唯一真源**：一个 store 管全站所有播放器，并负责与服务端对齐。
 *
 * ## 为什么不能是组件内 `useState`
 *
 * 第一版把开关做在 `VideoStage` 内部（`useState(muted)`）。用户实机点了一下分镜卡
 * 的喇叭，得到的是「这一张卡响了，隔壁十九张还是哑的」——**看不出开关到底管了什么**，
 * 而且要听另一镜还得再点一次。开关的语义是「我这次想听声音」，是**用户的意图**，
 * 不是某个播放器的局部状态；意图天然是全局的、并且该在刷新后还记得。
 *
 * ## 三态：`muted: boolean | null`
 *
 * `null` = 用户从未表过态 → 各播放器按自己的 `muted` prop 默认（分镜卡静音、
 * 预览页成片有声）。一旦用户点了任意一个喇叭，就变成具体布尔值并**全站生效**。
 * 这样既做到「全局」，又不会把预览页「成片必须有声」这条既有口径抹掉
 * （服务端那份说明见 `lib/settings/ui-preference`）。
 *
 * ## 写入是乐观的、失败不回滚
 *
 * 点下去先改本地（全站立刻一致），再异步 PUT 落库。写失败**不回滚**：
 * 那会让开关自己弹回去，看起来像「点了没反应」；声音是当前会话里立刻生效的东西，
 * 本地态才是用户当下看到的真相。失败只留一行 console，下一次点击会再写一次。
 *
 * ## 读取要防「几十个播放器一起请求」
 *
 * 分镜页一屏就有十几张卡，每张都挂一个 `VideoStage`。`hydrate()` 必须幂等：
 *   · `loaded` —— 已拿到服务端值，直接返回；
 *   · `inflight` —— 有请求在路上，复用同一个 Promise；
 *   · `lastAttemptAt` —— 失败后 15 秒内不再重试，避免服务不可达时每挂一个播放器
 *     就打一发请求（那会把本来就在重启的 dev 服务压得更死）。
 */
import { create } from "zustand";

import { apiFetch } from "@/lib/api-fetch";

/** 失败后多久才允许再试（毫秒） */
const RETRY_AFTER_MS = 15_000;

interface VideoAudioState {
  /** 全局声音开关：true=静音 / false=有声 / null=用户从未表过态 */
  muted: boolean | null;
  /** 服务端值是否已拿到（拿到后不再重复请求） */
  loaded: boolean;
  /** 切换（写本地 + 异步落库） */
  setMuted: (muted: boolean) => void;
  /** 按当前生效态取反（播放器传自己看到的那个值进来） */
  toggleFrom: (current: boolean) => void;
  /** 从服务端拉一次；幂等，可被任意多个播放器同时调用 */
  hydrate: () => Promise<void>;
}

/** 同一个 store 上的请求去重（模块级，跨组件实例共享） */
let inflight: Promise<void> | null = null;
let lastAttemptAt = 0;

/** 落库：失败只记一行日志，不影响本地已生效的状态 */
async function persistMuted(muted: boolean): Promise<void> {
  try {
    await apiFetch("/api/settings/video-audio", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ muted }),
    });
  } catch (err) {
    console.warn("[video-audio] 声音开关没能写入设置库（本次会话内仍全局生效）：", err);
  }
}

export const useVideoAudioStore = create<VideoAudioState>((set, get) => ({
  muted: null,
  loaded: false,

  setMuted: (muted) => {
    set({ muted });
    void persistMuted(muted);
  },

  toggleFrom: (current) => {
    get().setMuted(!current);
  },

  hydrate: () => {
    if (get().loaded) return Promise.resolve();
    if (inflight) return inflight;
    if (lastAttemptAt && Date.now() - lastAttemptAt < RETRY_AFTER_MS) return Promise.resolve();
    lastAttemptAt = Date.now();

    inflight = (async () => {
      try {
        const res = await apiFetch("/api/settings/video-audio", { cache: "no-store" });
        const data = (await res.json()) as { muted?: unknown };
        set({
          muted: typeof data.muted === "boolean" ? data.muted : null,
          loaded: true,
        });
      } catch (err) {
        // 读不到 = 按各播放器自己的默认处理，界面不需要任何提示：
        // 声音开关是「锦上添花」，读不到偏好不该弹一个红条打断看片。
        console.warn("[video-audio] 读取声音偏好失败，按播放器默认处理：", err);
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  },
}));
